import { useEffect, useState } from "react";
import { pubSub } from "../ably";
import { CHANNELS } from "../channel/channels.js";
import { reconstructActiveIds, classifyQnaMessage, classifyPollMessage } from "../channel/reconstructActiveIds.js";
import { reconcileActiveQnaIds, reconcileActivePoll, applyPollHistoryState } from "./adminStore";

export default function useAdminActiveHistorySync(sessionId) {
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    async function runOnce() {
      if (cancelled) return;
      const [qnaHistory, pollHistory] = await Promise.all([
        pubSub.getChannelHistoryWithMeta(CHANNELS.QNA),
        pubSub.getChannelHistoryWithMeta(CHANNELS.POLL),
      ]);
      if (cancelled) return;
      reconcileActiveQnaIds(sessionId, reconstructActiveIds(qnaHistory, classifyQnaMessage));
      reconcileActivePoll(sessionId, reconstructActiveIds(pollHistory, classifyPollMessage));
      applyPollHistoryState(sessionId, pollHistory);
    }

    if (pubSub.isConnected) runOnce();
    const handler = (connected) => {
      if (connected) runOnce();
    };
    pubSub.onConnectionChange(handler);
    return () => {
      cancelled = true;
      pubSub.offConnectionChange(handler);
    };
  }, [sessionId]);
}

export function useUserPollActiveIds(sessionId) {
  const [activeIds, setActiveIds] = useState(() => new Set());
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    async function loadHistory() {
      try {
        const pollHistory = await pubSub.getChannelHistoryWithMeta(CHANNELS.POLL);
        if (cancelled) return;
        const ids = reconstructActiveIds(pollHistory, classifyPollMessage);
        setActiveIds(new Set([...ids].map(String)));
      } catch { /* history read failure keeps the previously known ids */ }
    }
    loadHistory();
    const onPoll = (data) => {
      const cls = classifyPollMessage(data);
      if (!cls) return;
      if (cls.type === "upsert") {
        setActiveIds((prev) => new Set(prev).add(String(cls.id)));
      } else if (cls.type === "remove") {
        setActiveIds((prev) => {
          const n = new Set(prev);
          n.delete(String(cls.id));
          return n;
        });
      } else if (cls.type === "replace") {
        setActiveIds(new Set(cls.ids.map(String)));
      }
    };
    // Rewound history replays arrive through the same subscription; the
    // classification is idempotent so replayed and live messages converge.
    const handler = (data) => onPoll(data);
    pubSub.subscribe(CHANNELS.POLL, handler);
    const onConnect = (connected) => { if (connected) loadHistory(); };
    pubSub.onConnectionChange(onConnect);
    return () => {
      cancelled = true;
      pubSub.unsubscribe(CHANNELS.POLL, handler);
      pubSub.offConnectionChange(onConnect);
    };
  }, [sessionId]);
  return activeIds;
}
